import * as React from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FormTypes } from '@oneblink/types'
import { OneBlinkAppsError, submissionService } from '@oneblink/apps-react'
import { formElementsService } from '@oneblink/sdk-core'

import { createTrackingCode } from '../lib/trackingCode'
import { removeInfoElements } from '../lib/infoElementHelpers'
import serverValidation from '../lib/server-validation'
import useQuery from './useQuery'
import useIsDraftInApprovals from './useIsDraftInApprovals'
import useGetClarification from './useGetClarification'
import useAutoSaveKey from './useAutoSaveKey'
import { getFormSlugOrId } from '../providers/FormDefinitionsProvider'

import config from '../config'

interface Actions {
  onSubmit: (
    newFormSubmission: submissionService.NewFormSubmission,
    options?: { queryStringArgs: Record<string, string> },
  ) => Promise<void>
  clearServerValidationMessage: () => void
}

interface SubmissionState {
  isSubmitting: boolean
  serverValidationMessage?: string
}

/**
 * This function will retrieve the submission value from the root level with a meta prop receiptEmail
 * @param {Form} definition - the form
 * @param {Record<string, unknown>} submission - the submission
 * @returns
 */
function getMetaEmail(
  definition: FormTypes.Form,
  submission: Record<string, unknown>,
): string | undefined {
  const emailElement = formElementsService.findFormElement(
    definition.elements,
    (el) => {
      if (el.meta) {
        try {
          const meta = JSON.parse(el.meta)
          return meta.receiptEmail
        } catch (e) {
          return false
        }
      }
      return false
    },
  )
  if (emailElement && 'name' in emailElement) {
    return submission[emailElement.name] as string
  }
}

export default function useSubmitForm({
  skipServerValidation,
  formDefinition,
  preFillData,
}: {
  skipServerValidation: boolean
  formDefinition: FormTypes.Form
  preFillData: Record<string, unknown> | undefined
}): [Actions, SubmissionState] {
  const [{ isSubmitting, serverValidationMessage }, setSubmissionState] =
    React.useState<{ isSubmitting: boolean; serverValidationMessage?: string }>(
      { isSubmitting: false },
    )

  const history = useHistory()

  const {
    trackingCode: qsTrackingCode,
    previousFormSubmissionApprovalId: qsPreviousFormSubmissionApprovalId,
  } = useQuery()
  const [, fetchIsDraftInApprovals] = useIsDraftInApprovals()
  const [, { fetchClarification }] = useGetClarification()
  const autoSaveKey = useAutoSaveKey(preFillData)

  //need this for testing locally
  const paymentOrigin = React.useMemo(() => {
    if (window.location.hostname === 'localhost') {
      return 'https://google.com'
    }
    return window.location.origin
  }, [])

  const onSubmit: Actions['onSubmit'] = React.useCallback(
    async (newFormSubmission, options) => {
      const existingTrackingCode = qsTrackingCode as string
      const previousFormSubmissionApprovalId =
        qsPreviousFormSubmissionApprovalId as string
      const formSubmission: submissionService.FormSubmission = Object.assign(
        {},
        newFormSubmission,
        {
          formsAppId: config.FORMS_APP_ID,
          jobId: undefined,
          externalId: undefined,
          draftId: null,
          preFillFormDataId: null,
          previousFormSubmissionApprovalId,
        },
      )

      setSubmissionState({ isSubmitting: true })

      try {
        if (!skipServerValidation) {
          await serverValidation(
            formDefinition.serverValidation,
            formSubmission.submission,
            formDefinition,
          )
        }
      } catch (e) {
        console.log(e)
        setSubmissionState(() => ({
          isSubmitting: false,
          serverValidationMessage:
            e instanceof OneBlinkAppsError ? e.message : undefined,
        }))
        return
      }

      try {
        if (
          existingTrackingCode &&
          !previousFormSubmissionApprovalId &&
          (await fetchIsDraftInApprovals(existingTrackingCode))
        ) {
          throw new OneBlinkAppsError('Draft has already been submitted')
        }

        if (existingTrackingCode && previousFormSubmissionApprovalId) {
          if (
            !(await fetchClarification({
              previousFormSubmissionApprovalId,
              externalId: existingTrackingCode as string,
            }))
          ) {
            throw new OneBlinkAppsError(
              'This request for clarification may have already been completed',
            )
          }
        }

        const trackingCode = await createTrackingCode(
          formDefinition,
          existingTrackingCode,
        )

        formSubmission.submission.trackingCode = trackingCode
        formSubmission.externalId = trackingCode

        const newElements = removeInfoElements(
          formSubmission.definition.elements,
        )
        formSubmission.definition.elements = newElements

        const email =
          (formSubmission.submission.email as string) ||
          (formSubmission.submission.Email as string) ||
          getMetaEmail(formSubmission.definition, formSubmission.submission)

        const queryObj: {
          trackingCode: string
          submissionId?: string
          email?: string
        } = { trackingCode }

        if (email) {
          queryObj.email = email
        }

        const receiptSearchParams = new URLSearchParams(queryObj)

        if (options?.queryStringArgs) {
          Object.entries(options.queryStringArgs).forEach(([key, value]) => {
            if (!receiptSearchParams.get(key)) {
              receiptSearchParams.append(key, value)
            }
          })
        }

        const { submissionId, payment } = await submissionService.submit({
          autoSaveKey,
          formSubmission,
          shouldRunExternalIdGeneration: false,
          shouldRunServerValidation: true,
          isPendingQueueEnabled: false,
          paymentFormUrl: `${paymentOrigin}${window.location.pathname}`,
          paymentReceiptUrl: `${paymentOrigin}${
            window.location.pathname
          }/receipt?${receiptSearchParams.toString()}`,
        })

        if (!submissionId) {
          throw new Error(
            'Form submission unsuccessful - did not receive submission id',
          )
        }

        if (payment) {
          const url = new URL(payment.hostedFormUrl)
          history.replace(`${url.pathname}${url.search}`)
          setSubmissionState({ isSubmitting: false })
          return
        }

        history.push({
          pathname: `/forms/${getFormSlugOrId(formDefinition)}/receipt`,
          search: `?${new URLSearchParams({
            ...queryObj,
            ...options?.queryStringArgs,
            submissionId,
          }).toString()}`,
        })
      } catch (e) {
        let message = 'There was an error during submission'
        if (e instanceof OneBlinkAppsError) {
          if (e.httpStatusCode === 400 || e.httpStatusCode === 404) {
            message = e.message
          }
        }
        toast.error(message, {
          position: 'bottom-right',
          hideProgressBar: true,
        })
        setSubmissionState({ isSubmitting: false })
      }
    },
    [
      formDefinition,
      history,
      qsPreviousFormSubmissionApprovalId,
      autoSaveKey,
      fetchIsDraftInApprovals,
      fetchClarification,
      qsTrackingCode,
      paymentOrigin,
      skipServerValidation,
    ],
  )

  const clearServerValidationMessage = React.useCallback(() => {
    setSubmissionState((current) => {
      return { ...current, serverValidationMessage: undefined }
    })
  }, [])

  return [
    { onSubmit, clearServerValidationMessage },
    { isSubmitting, serverValidationMessage },
  ]
}
